import { createSelector } from 'reselect';

import { getAreActivitiesEnabled } from 'lib/selectors/getAreActivitiesEnabled';
import { getLatestActivitySetActivities } from 'lib/selectors/getLatestActivitySetActivities';
import { getIsInitialActivitySetPollLoading } from 'lib/selectors/getIsInitialActivitySetPollLoading';
import { getSetupActivityTypeRefs } from 'lib/selectors/getSetupActivityTypeRefs';
import { getCheckIsSetupActivityTypeRef } from 'lib/selectors/getCheckIsSetupActivityTypeRef';
import { getJobRun } from 'lib/selectors/getJobRun';
import { getPostLaborUpdatesOnNats } from 'lib/selectors/getPostLaborUpdatesOnNats';
import { getWorkOrderManagement } from 'lib/selectors/getWorkOrderManagement';
import { getAggClosedActivitiesMs } from 'lib/utils/activities';
import { diff } from 'lib/utils/date';

/*
  note that this selector returns agg for ENTIRE set, not just those within
  scope... this is an important distinction for when we need entire setup agg (ie
  showing setup in progress in bar); if you need scoped activity agg ms, use the
  scoped agg selectors
*/

const getClosedSetupAggMs = createSelector(
  [
    getAreActivitiesEnabled,
    getLatestActivitySetActivities,
    getIsInitialActivitySetPollLoading,
    getSetupActivityTypeRefs,
    getCheckIsSetupActivityTypeRef,
    getJobRun,
    getPostLaborUpdatesOnNats,
    getWorkOrderManagement,
  ],
  (
    areActivitiesEnabled,
    latestActivitySetActivities,
    isInitialActivitySetPollLoading,
    setupActivityTypeRefs,
    checkIsSetupActivityTypeRef,
    jobRun,
    natsFlowEnabled,
    workOrderManagement
  ) => {
    // NATS mode: calculate closed setup time from closed setup labor tickets
    if (natsFlowEnabled && areActivitiesEnabled) {
      const workOrderOperationLaborTickets =
        workOrderManagement?.workOrderOperationLaborTickets;

      if (!workOrderOperationLaborTickets?.length) {
        return 0;
      }

      const closedSetupTickets = workOrderOperationLaborTickets.filter(
        (ticket) => {
          return ticket.type?.toLowerCase() === 'setup' && ticket.clockOut;
        }
      );

      if (!closedSetupTickets.length) {
        return 0;
      }

      // Calculate total elapsed time from all closed setup tickets
      return closedSetupTickets.reduce((total, ticket) => {
        const elapsed = diff(ticket.clockOut, ticket.clockIn, 'milliseconds');
        return total + Math.max(elapsed, 0);
      }, 0);
    }

    if (areActivitiesEnabled) {
      if (
        isInitialActivitySetPollLoading ||
        (setupActivityTypeRefs && !setupActivityTypeRefs.length) ||
        (latestActivitySetActivities && !latestActivitySetActivities.length)
      ) {
        return 0;
      }
      return getAggClosedActivitiesMs({
        activities: latestActivitySetActivities,
        filterCb: ({ activityTypeRef }) => {
          return checkIsSetupActivityTypeRef(activityTypeRef);
        },
      });
    }
    if (!jobRun || !jobRun.setupEnd) {
      return 0;
    }
    return diff(jobRun.setupEnd, jobRun.startDate, 'milliseconds');
  }
);

export { getClosedSetupAggMs };
